import { Help, Info } from "@mui/icons-material";
import { Box, Chip, styled } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { Mortgage, OtherMortgage } from "../../../../../model/MortgageExtensionModel";
import { useFinanceCalculatorService } from "../../../../../services/finance-calculator/FinanceCalculatorServiceContext";
import { addYears, DateFormat, formatDate } from "../../../../../utils/DateUtils";
import { formatAmount } from "../../../../../utils/NumberUtils";
import { renderProductType } from "../../../../../utils/Utils";
import MortgageTooltip from "../../../../tooltip/MortgageTooltip";
import {
    MortgageSummaryBold,
    MortgageSummaryHeaderLabel,
    MortgageSummaryLabel,
} from "../../../2-summary/NewMortgageSummary";

const MobileChip = styled(Chip)(({ theme }) => ({
    borderRadius: 2,
    height: "18px",
}));

const MortgageBoxMobile = styled(Box)(({}) => ({
    padding: "16px",
    borderTop: "1px solid rgba(0,0,0,0.12)",
    "&:last-child": {
        borderBottom: "1px solid rgba(0,0,0,0.12)",
    },
}));

const MortgageRowMobile = (props: { mortgage: Mortgage | OtherMortgage; isNew: boolean }) => {
    const { mortgage, isNew } = props;
    const { i18n, t } = useTranslation();
    const financeCalculatorService = useFinanceCalculatorService();

    const backgroundColor = isNew ? "#0093D1" : "#B4D7FA";
    const textColor = isNew ? "#fff" : "rgba(0, 0, 0, 0.54)";
    const label = isNew ? t("General.New") : t("General.Existing");

    function monthlyPayment(): number {
        if (isNew) {
            return (mortgage as Mortgage).monthlyPayment;
        }
        return mortgage.interestRate > 0
            ? financeCalculatorService.calcMonthlyAmount(mortgage.amount, mortgage.interestRate)
            : 0;
    }

    function printExpirationDate() {
        if (isNew) {
            return addYears(mortgage.expirationDate, +mortgage.duration);
        }
        return mortgage.expirationDate !== null && "hypothek_var" !== mortgage.productType
            ? formatDate(mortgage.expirationDate, DateFormat.DATE)
            : null;
    }

    function printPosition() {
        if (isNew) {
            return null;
        }
        return (
            <MortgageSummaryHeaderLabel>
                {" "}
                {t("General.Position")} {mortgage.mortgagePosition}{" "}
            </MortgageSummaryHeaderLabel>
        );
    }

    return (
        <MortgageBoxMobile borderLeft={`4px solid ${backgroundColor}`}>
            <Box sx={{ marginBottom: "8px" }} key="isNew">
                <MobileChip label={label} size={"small"} sx={{ backgroundColor: backgroundColor, color: textColor }} />
            </Box>
            <Box key="productType">
                <MortgageSummaryBold> {renderProductType(mortgage, i18n, t)}</MortgageSummaryBold>
            </Box>
            <Box sx={{ marginBottom: "16px" }} key="duration">
                <MortgageSummaryLabel>
                    {mortgage.duration} {t("General.Years")} / {printExpirationDate()}
                </MortgageSummaryLabel>
                {printPosition()}
            </Box>
            <Box display={"flex"} flexWrap={"wrap"} key="otherInfo">
                <Box flex={"50%"} sx={{ paddingBottom: "16px" }}>
                    <Box>
                        <MortgageSummaryHeaderLabel>{t("General.Monthly charge")}</MortgageSummaryHeaderLabel>
                    </Box>
                    <Box>
                        <MortgageSummaryLabel>
                            {mortgage.currency} {formatAmount(monthlyPayment())}
                            <MortgageTooltip title={t("General.This calculation is indicative and is based on...")}>
                                <Info
                                    sx={{ fontSize: "16px", color: "#0093D1", marginLeft: "4px", marginBottom: "1px" }}
                                />
                            </MortgageTooltip>
                        </MortgageSummaryLabel>
                    </Box>
                </Box>
                <Box flex={"50%"} sx={{ paddingBottom: "16px" }}>
                    <Box>
                        <MortgageSummaryHeaderLabel>{t("General.Interest rate")}</MortgageSummaryHeaderLabel>
                    </Box>
                    <Box>
                        <MortgageSummaryLabel>
                            {formatAmount(mortgage.interestRate)} %
                            <MortgageTooltip
                                title={t(
                                    "ConfigurationScreen.Interest rates may change over the course of the next few days..."
                                )}>
                                <Help
                                    sx={{ fontSize: "16px", color: "#0093D1", marginLeft: "4px", marginBottom: "1px" }}
                                />
                            </MortgageTooltip>
                        </MortgageSummaryLabel>
                    </Box>
                </Box>
                {mortgage.amortizations &&
                    mortgage.amortizations.map((amortization, index) => (
                        <Box flex={"50%"} sx={{ paddingBottom: "16px" }} key={`amortization-${index}`}>
                            <Box>
                                <MortgageSummaryHeaderLabel>{amortization.type}</MortgageSummaryHeaderLabel>
                            </Box>
                            <Box>
                                <MortgageSummaryHeaderLabel>{t("General.Monthly charge")}</MortgageSummaryHeaderLabel>
                            </Box>
                            <Box>
                                <MortgageSummaryLabel>
                                    {mortgage.currency} {formatAmount(amortization.monthlyAmount)}
                                </MortgageSummaryLabel>
                            </Box>
                        </Box>
                    ))}
                <Box flex={"100%"} sx={{ textAlign: "right" }} key="total">
                    <MortgageSummaryBold>
                        {mortgage?.currency} {formatAmount(mortgage.amount)}
                    </MortgageSummaryBold>
                </Box>
            </Box>
        </MortgageBoxMobile>
    );
};

export default MortgageRowMobile;
